import { Component, ElementRef, AfterViewInit, Input, Output, EventEmitter,
    ViewChildren, QueryList } from '@angular/core';
import { MonthType } from './month-type';
import { CalendarDay } from './calendar-day';
import { Day } from './day';
import { Anniversary } from './anniversary';

@Component({
  selector: 'calendar',
  templateUrl: './calendar.html',
  styleUrls: ['./calendar.scss'],
})

/**
 * 달력 컴포넌트 클래스
 */
export class Calendar implements AfterViewInit {

  /**
   * 요일 출력 스타일 기본값
   */
  private static WEEK_TITLE_ENG: string[] = [
    'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT',
  ];

  /**
   * 현재 달 출력의 꼬릿말
   */
  private static MONTH_TAIL: string = '월';

  /**
   * 선택한 날짜 element에 부여할 스타일 클래스 이름
   */
  private static STYLE_CLASS_SELECTED: string = 'selected';

  /**
   * 이전 달 날짜에 부여할 스타일 클래스 이름
   */
  private static STYLE_PAST: string = 'past';

  /**
   * 다음 달 날짜에 부여할 스타일 클래스 이름
   */
  private static STYLE_FUTURE: string = 'future';

  /**
   * 오늘 날짜에 부여할 스타일 클래스 이름
   */
  private static STYLE_TODAY: string = 'today';

  /**
   * 날짜 elements
   */
  @ViewChildren(CalendarDay) private $calendarDays: QueryList<CalendarDay>;

  /**
   * 날짜 선택 후 event emitter
   */
  @Output('onDaySelected') private $onDaySelected: EventEmitter<any> = new EventEmitter();

  /**
   * 월 변경 후 event emiiter
   */
  @Output('onMonthChanged') private $onMonthChanged: EventEmitter<Date> = new EventEmitter<Date>();

  /**
   * 마지막으로 선택한 날짜 Div element
   * 달력이 새로 그려질 때 마다 참조를 해제함
   */
  private $latestSelectedDayDiv: HTMLElement;

  /**
   * 디바이스의 오늘 날짜
   */
  private $today: Date;

  /**
   * 선택한 달력의 날짜
   * renderCalendar() 호출 시 이 날짜의 정보를 기준으로 그림
   */
  private $curr: Date;

  /**
   * 선택한 달력의 주 수
   * renderCalendar() 내에서 구해져 저장됨
   */
  private $weeks: number;

  /**
   * 현재 달 출력에 붙일 꼬릿말
   */
  private $monthTail: string;

  /**
   * 기념일을 짧게 출력할 기준, 1 이상일 경우만 제대로 적용
   */
  private $shortAnniversary: number;

  /**
   * 기념일 목록
   */
  private $anniversaries: Anniversary[];

  /**
   * 요일 출력 스타일 (SUN, MON, TUE ...), 따라서 배열의 크기가 반드시 7 이어야 함
   */
  weekTitles: string[];

  /**
   * 현재 달력의 '월' 출력 문자열
   */
  currMonth: string;

  /**
   * 이전 달 출력 문자열
   */
  prevMonth: string;

  /**
   * 다음 달 출력 문자열
   */
  nextMonth: string;

  /**
   * 현재 달력에 포함되는 모든 날짜
   * 1일이 월요일 이상이라면 (일-월-화 순서 기준) 이전 요일에 해당하는 이전 달의 날짜와
   * 마지막 날짜가 금요일 이하라면 다음 요일에 해당하는 다음 달의 날짜 그리고 이번 달의 날짜들을 포함함
   * view에서 이 배열을 순회하여 출력함
   */
  days: Day[];

  /**
   * @constructor
   * @param {ElementRef} el AngularJS DI를 통해 주입됨
   */
  constructor(el: ElementRef) {
    this.$today = new Date();
    this.$curr = new Date(this.$today.getTime());
    this.$monthTail = Calendar.MONTH_TAIL;
    this.$shortAnniversary = 0;
    this.$anniversaries = [];
    this.weekTitles = Calendar.WEEK_TITLE_ENG;
  }

  /**
   * 컴포넌트 초기화 후 달력을 그림
   * @override
   */
  ngAfterViewInit() {
    this.renderCalendar();
  }

  /**
   * 오늘 날짜 선택
   */
  public selectToday() {
    // tslint:disable-next-line:no-this-assignment
    const thisRef = this;
    thisRef.$calendarDays.toArray().forEach((item) => {
      if (item.day.isToday) {
        thisRef.select(item);
      }
    });
  }

  /**
   * 기념일 설정 시 달력 다시 그림
   */
  @Input('anniversaries') set updateAnniversaries(anniversaries: Anniversary[]) {
    this.$anniversaries = anniversaries;
    this.renderCalendar();
  }

  @Input('short-anniversary') set updateShortAnniversary(n: number) {
    this.$shortAnniversary = n;
  }

  /**
   * $curr의 정보를 기반으로 달력 그림
   */
  private renderCalendar() {
    const year: number = this.$curr.getFullYear();
    const month: number = this.$curr.getMonth();
    const weeks: number = this.getWeeksInMonth(year, month);
    const days: number = this.getDaysInMonth(year, month);
    // 1일의 서수, 일 == 0, ..., 토 == 6
    const firstDayOrdinal: number = new Date(year, month, 1).getDay();
    let oneMonthAgo: Date = null;
    let oneMonthLater: Date = null;
    let newDays: Day[] = null;

    // 참조하고 있던 Element 참조 해제
    if (this.$latestSelectedDayDiv) {
      this.$latestSelectedDayDiv = null;
    }

    // 한 달 전, 후 정보 획득
    oneMonthAgo = new Date(this.$curr.getTime());
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    oneMonthLater = new Date(this.$curr.getTime());
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

    // 헤더 부분 갱신
    this.currMonth = (this.$curr.getMonth() + 1) + this.$monthTail;
    this.prevMonth = `${oneMonthAgo.getFullYear()}.${this.addZero(oneMonthAgo.getMonth() + 1)}`;
    this.nextMonth = `${oneMonthLater.getFullYear()}.${this.addZero(oneMonthLater.getMonth() + 1)}`;

    // 날짜 부분 데이터 구조 완성
    // 달력을 채우기 위해서 루프를 주 수 * 7로 함
    // 1일의 서수의 역수부터 시작하여 0은 1일이 되고, 0 미만은 이전 날짜, 이 달의 날짜 수를 초과하면 다음 날짜를 의미함
    // TODO: i++ 해결하기
    newDays = [];

    for (let i = 0, d = -firstDayOrdinal + 1; i <  weeks * 7; i += 1, d = i - firstDayOrdinal + 1) {
      const date: Date = new Date(this.$curr.getFullYear(), this.$curr.getMonth(), d);
      let type: MonthType = null;
      let dayObj: Day = null;
      let isToday: boolean = false;
      const anniversary: Anniversary[] = [];

      // 날짜 객체 생성 준비
      // 달 유형
      type = d < 1 ? MonthType.PREV_MONTH :
          (d > days ? MonthType.NEXT_MONTH : MonthType.THIS_MONTH);
      // 기념일 정보, 기념일 목록을 순회하여 일치하는 날짜가 있고, 이번 달이면 저장
      if (this.$anniversaries) {
        for (let anniv: Anniversary, j = 0; anniv = this.$anniversaries[j]; j += 1) {
          if (anniv.equals(date) && type === MonthType.THIS_MONTH) {
            anniversary.push(anniv);
          }
        }
      }

      // 날짜 객체 생성
      dayObj = new Day(date, type, anniversary);
      // 기념일이 일정 갯수 이상이라면 짧게 출력 여부 설정
      if (this.$shortAnniversary > 0 && anniversary.length >= this.$shortAnniversary) {
        dayObj.isDisplayShortAnniversary = true;
      }

      // 날짜 객체 추가 정보 저장
      // 날짜가 오늘인지 여부
      isToday = this.$today.getFullYear() === year &&
        this.$today.getMonth() === month &&
        this.$today.getDate() === d;
      if (isToday) {
        dayObj.isToday = true;
      }

      // 날짜 목록에 추가
      newDays.push(dayObj);
    }
    this.days = newDays;

    // 주 수 저장
    this.$weeks = weeks;
  }

  /**
   * 해당 달의 날 수를 반환
   * @param {number} year 년
   * @param {number} month 월 0-11
   * @return {number} 해당 년 월의 날 수
   */
  private getDaysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  /**
   * 해당 달의 주 수를 반환
   * @param {number} year 년
   * @param {number} month 월 0-11
   * @return {number} 해당 달의 주 수를 반환
   */
  private getWeeksInMonth(year: number, month: number): number {
    const firstDayOrdinal = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    return Math.ceil((days + firstDayOrdinal) / 7);
  }

  /**
   * 10미만 정수라면 '0'을 붙여서 반환
   */
  private addZero(n: number): string {
    return n < 10 ? '0' + Math.ceil(n) : '' + Math.ceil(n);
  }

  /**
   * 현재 날짜의 달 증감
   */
  private incrementMonth(monthScalar: number) {
    this.$curr.setMonth(this.$curr.getMonth() + monthScalar);
    this.$curr.setDate(1);
    this.renderCalendar();
    this.$onMonthChanged.emit(this.$curr);
  }

  /**
   * 날짜에 사용할 스타일 클래스 이름 목록을 반환
   */
  classOf(day: Day): string[] {
    // 날짜의 기념일 이름과 이전, 다음 달 정보를 함께 반환
    const styles = day.anniversaryTypes;
    if (day.isPast) { styles.push(Calendar.STYLE_PAST); }
    if (day.isFuture) { styles.push(Calendar.STYLE_FUTURE); }
    if (day.isToday) { styles.push(Calendar.STYLE_TODAY); }
    return styles;
  }

  /**
   * 현재 달력을 다음 달로 이동
   */
  public next() {
    this.incrementMonth(1);
  }

  /**
   * 현재 날짜로 이동
   */
  public current() {
    this.$curr = new Date();
    this.renderCalendar();
    this.$onMonthChanged.emit(this.$curr);
  }

  /**
   * 현재 달력을 이전 달로 이동
   */
  public prev() {
    this.incrementMonth(-1);
  }

  /**
   * 현재 달력이 5개의 주로 이루어졌는지 여부
   */
  public get isFiveWeeks(): boolean {
    return this.$weeks === 5;
  }

  /**
   * 현재 달력이 6개의 주로 이루어졌는지 여부
   */
  public get isSixWeeks(): boolean {
    return this.$weeks === 6;
  }

  /**
   * 날짜 선택 (클릭) 이벤트
   * output event emitter을 호출하여 상위 컴포넌트에 전파
   */
  public select(component: CalendarDay) {
    const currentTarget = component.el.nativeElement;
    const day = component.day;

    // 이전 또는 다음 달인 경우 예외
    if (!day.isThisMonth) return;
    // 이미 선택한 날짜거나 없으면 예외
    if (this.$latestSelectedDayDiv === currentTarget) return;

    // 선택 스타일 클래스 갱신 후 선택한 DIV 저장
    if (this.$latestSelectedDayDiv) {
      this.$latestSelectedDayDiv.classList.remove(Calendar.STYLE_CLASS_SELECTED);
    }
    currentTarget.classList.add(Calendar.STYLE_CLASS_SELECTED);
    this.$latestSelectedDayDiv = currentTarget;

    // event emit
    // 선택한 날짜의 날짜 객체와 기념일 정보를 함께 보냄
    this.$onDaySelected.emit(
      { date: day.date, anniversaries: day.anniversaries });
  }
}
